import { ListKeyManagerOption } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import type { Location } from '@vendasta/protos/atlas/v1/types';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, firstValueFrom, map, shareReplay } from 'rxjs';
import { isPathBlockListed, LOCATION_SWITCH_CONFIG_TOKEN } from '../../../../business-nav.module';
import { BusinessNavConfigService, Config } from '../../../../config.service';
import { BusinessNavDataService } from '../../../../data.service';
import { BusinessNavHostBridge } from '../../../../host-bridge';
import { BusinessNavLocationService } from '../../location.service';

enum AccountType {
  brand = 'account/brands',
  location = 'account/location',
  unset = 'account/unset',
}
const AccountTypes = Object.values(AccountType);

const ID_PLACEHOLDER = 'id_placeholder';

@Component({
  selector: 'bc-location-list-item',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatListModule, MatMenuModule, TranslateModule, GalaxyBadgeModule],
  templateUrl: './location-list-item.component.html',
  styleUrls: ['./location-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LocationListItemComponent implements ListKeyManagerOption {
  location = input.required<Location>();
  isActive = input(false);
  iconUrl = input<string | null>(null);
  selected = output();
  /** Never disabled for now, this is here for the interface implementation. */
  disabled = false;

  readonly dataService = inject(BusinessNavDataService);
  private readonly config = inject(BusinessNavConfigService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly elRef = inject(ElementRef);

  private readonly bridge = inject(BusinessNavHostBridge);
  private readonly liveConfig = toSignal<Config>(this.config.config$, { requireSync: true });
  private readonly serviceProviderId = computed(() => this.liveConfig().serviceProviderId);
  protected readonly isCurrentLocation = computed(() => {
    const sameAccountGroup =
      !!this.location().accountGroup && this.liveConfig().accountId === this.location().accountGroup.accountGroupId;
    const sameGroup =
      !!this.location().brand && this.liveConfig().groupPath === this.location().brand.pathNodes.join('|');
    return sameAccountGroup || sameGroup;
  });

  private readonly businessNavLocationService = inject(BusinessNavLocationService);

  protected readonly hasProduct$ = combineLatest([
    this.businessNavLocationService.activePath$,
    this.config.config$,
  ]).pipe(
    map(([, config]) => {
      if (!this.location().accountGroup || !this.location().accountGroup.activatedProductIds) {
        return false;
      }

      return this.location().accountGroup.activatedProductIds.some(
        (activatedProductId) => activatedProductId === config.serviceProviderId,
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly defaultLocation$ = this.dataService.defaultLocation$;
  protected readonly isDefault$ = this.defaultLocation$.pipe(
    map(
      (defaultLocation) =>
        (this.location().accountGroup &&
          defaultLocation?.accountGroupId === this.location().accountGroup.accountGroupId) ||
        (this.location().brand && defaultLocation?.groupId === this.location().brand.pathNodes.join('|')),
    ),
  );

  private readonly locationSwitchConfig = inject(LOCATION_SWITCH_CONFIG_TOKEN);

  setDefaultLocation(accountGroupId?: string, groupId?: string): void {
    this.dataService.setDefaultLocation(accountGroupId, groupId).subscribe({
      error: () => this.snackbarService.openErrorSnack('COMMON.RESULT.FAILURE'),
    });
  }

  /** Gets the label for this option. */
  getLabel(): string {
    if (this.location().accountGroup) {
      return this.location().accountGroup.name;
    } else if (this.location().brand) {
      return this.location().brand.name;
    }
    // Location not provided yet, should probably not get here
    return '';
  }

  async goToLocation(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.selected.emit();
    if (this.isCurrentLocation()) {
      return;
    }
    if (this.bridge.locationSelected(this.location(), this.liveConfig())) {
      return;
    }

    const params = collectRouteParams(this.router);

    const configForContext = this.locationSwitchConfig.supportedLocationContextConfig.find(
      (config) =>
        config.context ===
        (this.router.url.includes('location') || this.location().accountGroup ? 'location' : 'brand'),
    );

    const canSwitchWithinServiceProvider =
      this.serviceProviderId().toUpperCase() === 'VBC' || (await firstValueFrom(this.hasProduct$));

    if (!configForContext || !canSwitchWithinServiceProvider) {
      const url = this.location().accountGroup?.url || this.location().brand?.url;
      window.location.href = url + '/home';
      return;
    }

    const replaceId = params[configForContext.locationParamKey] || ID_PLACEHOLDER;
    const [isBlockListed, destUrl] = isPathBlockListed(this.locationSwitchConfig, this.router.url);
    if (isBlockListed) {
      const loadingPath = this.interpolateUrl(
        this.locationSwitchConfig.loadingPath || this.locationSwitchConfig.defaultPath,
        ID_PLACEHOLDER,
      );
      const loadingUrl = this.router.createUrlTree([loadingPath]);
      let interpolatedUrl = '';
      if (destUrl.includes(replaceId)) {
        interpolatedUrl = this.interpolateUrl(destUrl, replaceId);
      } else {
        interpolatedUrl = this.interpolateUrl(destUrl, ID_PLACEHOLDER);
      }
      return this.navigateToUrl(interpolatedUrl, { loadPath: loadingUrl });
    }

    if (!this.router.url.includes(replaceId)) {
      const url = this.router.createUrlTree([this.locationSwitchConfig.defaultPath]).toString();
      return this.navigateToUrl(this.interpolateUrl(url, ID_PLACEHOLDER), {});
    }

    const url = this.interpolateUrl(this.router.url, replaceId);
    return this.navigateToUrl(url, {});
  }

  onScroll(): void {
    this.elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private interpolateUrl(url: string, replaceId: string): string {
    const useCategory = this.location().accountGroup ? AccountType.location : AccountType.brand;
    const interpolated = AccountTypes.reduce((acc, category) => acc.replace(category, useCategory), url);

    const useId = this.location().accountGroup
      ? this.location().accountGroup.accountGroupId
      : this.location().brand.pathNodes.join('|');

    return interpolated.replace(replaceId, useId);
  }

  private async navigateToUrl(url: string, { loadPath }: { loadPath?: UrlTree }) {
    if (this.serviceProviderId().toUpperCase() === 'VBC' && !loadPath) {
      return this.router.navigateByUrl(url);
    } else if (this.serviceProviderId().toUpperCase() === 'VBC' && loadPath) {
      return this.router
        .navigateByUrl(loadPath, {
          skipLocationChange: true,
          state: { target: url },
        })
        .then((leftPage) => (leftPage ? this.router.navigateByUrl(url) : leftPage));
    } else {
      window.location.href = url;
    }
  }
}

function collectRouteParams(router: Router) {
  let params = {};
  const stack: ActivatedRouteSnapshot[] = [router.routerState.snapshot.root];
  while (stack.length > 0) {
    const route = stack.pop();
    params = { ...params, ...route.params };
    stack.push(...route.children);
  }
  return params;
}
